import toast from 'react-hot-toast';

const CART_KEY = 'cartItems';

interface CartItem {
    productId: string;
    quantity: number;
    affiliateCode?: string | null;
}

// Récupérer le code affiliate depuis l'URL (optionnel)
const getAffiliateFromUrl = (): string | null => {
    const params = new URLSearchParams(window.location.search);
    return params.get('affiliate');
};

// Normaliser les données du panier
const getCart = (): { items: CartItem[] } => {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return { items: [] };
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return { items: parsed };
        }
        if (parsed && Array.isArray(parsed.items)) {
            return parsed;
        }
        return { items: [] };
    } catch {
        return { items: [] };
    }
};

const saveCart = (cart: { items: CartItem[] }) => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
};

// Ajouter un produit au panier
export const addToCart = (productId: string, quantity: number = 1): boolean => {
    try {
        const cart = getCart();
        const existing = cart.items.find(item => item.productId === productId);
        const affiliateCode = getAffiliateFromUrl();

        if (existing) {
            existing.quantity += quantity;
            if (affiliateCode) existing.affiliateCode = affiliateCode;
            toast.success(`Quantité mise à jour : ${existing.quantity} article(s)`, {
                duration: 1500,
                position: 'top-right',
                style: {
                    background: '#16f19e',
                    color: '#0A0A0A',
                    padding: '8px',
                    borderRadius: '8px',
                },
            });
        } else {
            cart.items.push({
                productId,
                quantity,
                affiliateCode: affiliateCode || null,
            });
            toast.success('Produit ajouté au panier', {
                duration: 1500,
                position: 'top-right',
                style: {
                    background: '#16f19e',
                    color: '#0A0A0A',
                    padding: '8px',
                    borderRadius: '8px',
                },
            });
        }
        saveCart(cart);
        window.dispatchEvent(new Event('cartUpdated'));
        return true;
    } catch (error) {
        console.error('Erreur ajout panier:', error);
        toast.error('Erreur', {
            duration: 1500,
            position: 'top-right',
            style: {
                background: '#EF2535',
                color: '#ffffff',
                padding: '8px',
                borderRadius: '8px',
            },
        });
        return false;
    }
};

// Retirer un produit du panier
export const removeFromCart = (productId: string): boolean => {
    try {
        const cart = getCart();
        const initialLength = cart.items.length;
        cart.items = cart.items.filter(item => item.productId !== productId);
        if (cart.items.length < initialLength) {
            saveCart(cart);
            toast.success('Produit retiré du panier', {
                duration: 1500,
                position: 'top-right',
                style: {
                    background: '#16f19e',
                    color: '#0A0A0A',
                    padding: '8px',
                    borderRadius: '8px',
                },
            });
            window.dispatchEvent(new Event('cartUpdated'));
            return true;
        }
        return false;
    } catch (error) {
        console.error('Erreur retrait panier:', error);
        toast.error('Erreur', {
            duration: 1500,
            position: 'top-right',
            style: {
                background: '#EF2535',
                color: '#ffffff',
                padding: '8px',
                borderRadius: '8px',
            },
        });
        return false;
    }
};

// Obtenir tous les articles du panier
export const getCartItems = (): CartItem[] => {
    try {
        const cart = getCart();
        return cart.items;
    } catch {
        return [];
    }
};

// Compter le nombre total d'articles (somme des quantités)
export const getCartItemsCount = (): number => {
    try {
        const items = getCartItems();
        return items.reduce((total, item) => total + item.quantity, 0);
    } catch {
        return 0;
    }
};

// Basculer (ajouter/retirer) – utile pour un bouton toggle
export const toggleCart = (productId: string, quantity: number = 1): boolean => {
    const items = getCartItems();
    const exists = items.some(item => item.productId === productId);
    if (exists) {
        return removeFromCart(productId);
    } else {
        return addToCart(productId, quantity);
    }
};


export const updateCartQuantity = (productId: string, newQuantity: number): boolean => {
    try {
        const cart = getCart();
        const item = cart.items.find(item => item.productId === productId);
        if (item) {
            if (newQuantity <= 0) {
                // Si la nouvelle quantité est 0 ou moins, on supprime l'item
                cart.items = cart.items.filter(i => i.productId !== productId);
                toast.success('Produit retiré du panier', {
                    duration: 1500,
                    position: 'top-right',
                    style: {
                        background: '#16f19e',
                        color: '#0A0A0A',
                        padding: '8px',
                        borderRadius: '8px',
                    },
                });
            } else {
                item.quantity = newQuantity;
                toast.success(`Quantité mise à jour : ${newQuantity} article(s)`, {
                    duration: 1500,
                    position: 'top-right',
                    style: {
                        background: '#16f19e',
                        color: '#0A0A0A',
                        padding: '8px',
                        borderRadius: '8px',
                    },
                });
            }
            saveCart(cart);
            window.dispatchEvent(new Event('cartUpdated'));
            return true;
        }
        return false;
    } catch (error) {
        console.error('Erreur mise à jour quantité:', error);
        toast.error('Erreur', {
            duration: 1500,
            position: 'top-right',
            style: {
                background: '#EF2535',
                color: '#ffffff',
                padding: '8px',
                borderRadius: '8px',
            },
        });
        return false;
    }
};


/**
 * 🗑️ VIDER COMPLÈTEMENT LE PANIER
 */
export const clearCart = (): boolean => {
    try {
        localStorage.setItem(CART_KEY, JSON.stringify({ items: [] }));
        toast.success('Panier vidé avec succès', {
            duration: 1500,
            position: 'top-right',
            style: {
                background: '#16f19e',
                color: '#0A0A0A',
                padding: '8px',
                borderRadius: '8px',
            },
        });
        window.dispatchEvent(new Event('cartUpdated'));
        return true;
    } catch (error) {
        console.error('Erreur vidage panier:', error);
        toast.error('Erreur lors du vidage du panier', {
            duration: 1500,
            position: 'top-right',
            style: {
                background: '#EF2535',
                color: '#ffffff',
                padding: '8px',
                borderRadius: '8px',
            },
        });
        return false;
    }
};